import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOTS } from '@/components/RapidSOSMark/RapidSOSMark';

interface WelcomeIntroProps {
  /** Studio has signed the user in and is mounted underneath. */
  ready: boolean;
  onDone: () => void;
}

// Signals per mark dot; each dot grows as its signals land.
const SIGNALS_PER_DOT = 7;
const BLINK_MS = 600;
const TRAVEL_START_MS = 700;
const WHITEN_START_MS = 2100;
const WHITEN_MS = 380;
const CANVAS_END_MS = 2600;
const INTRO_MS = 3700;
const REDUCED_INTRO_MS = 1000;
const FADE_MS = 500;
// While Studio is still loading after the intro, signals keep arriving from
// the edges and light up the dot they land on, in place of a spinner.
const IDLE_SPAWN_MS = 140;
const IDLE_TRAVEL_MS = 1100;
const IDLE_FLASH_MS = 650;
const SIGNAL_RGB = '0,245,0';
const MARK_RGB = [250, 250, 250] as const;

interface IdleSignal {
  sx: number;
  sy: number;
  bend: number;
  dot: number;
  start: number;
}

/** A point on the screen's edge, as fractions of its width and height. */
function edgePoint(): [number, number] {
  const along = Math.random();
  switch (Math.floor(Math.random() * 4)) {
    case 0:
      return [along, 0];
    case 1:
      return [1, along];
    case 2:
      return [along, 1];
    default:
      return [0, along];
  }
}

function signalPosition(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  bend: number,
  u: number,
): [number, number] {
  const cx = (sx + tx) / 2 - (ty - sy) * bend;
  const cy = (sy + ty) / 2 + (tx - sx) * bend;
  const e = easeInOutCubic(u);
  const m = 1 - e;
  return [m * m * sx + 2 * m * e * cx + e * e * tx, m * m * sy + 2 * m * e * cy + e * e * ty];
}

interface Signal {
  sx: number;
  sy: number;
  bend: number;
  dot: number;
  appear: number;
  start: number;
  duration: number;
  phase: number;
}

function markGeometry(width: number, height: number) {
  // Keep in step with `.welcome-intro__lockup` in index.css.
  const size = Math.min(0.16 * Math.min(width, height), 140);
  const scale = size / 38;
  const left = width / 2 - size / 2;
  const top = height * 0.4 - size / 2;
  return {
    radius: 3.8 * scale,
    targets: DOTS.map(([cx, cy]) => [left + cx * scale, top + cy * scale] as const),
  };
}

function easeInOutCubic(u: number) {
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

function sizeCanvas(canvas: HTMLCanvasElement) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext('2d');
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, width, height };
}

function drawMark(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const { radius, targets } = markGeometry(width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = `rgb(${MARK_RGB.join(',')})`;
  for (const [x, y] of targets) {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Plays once right after sign-in: green signals blink on across the screen,
 * stream into the twelve dots of the RapidSOS mark, and "RapidSOS Studio"
 * rises in beneath it. Studio loads underneath, so the intro costs no time;
 * if Studio is not ready when the intro ends, it holds on the last frame.
 */
export function WelcomeIntro({ ready, onDone }: WelcomeIntroProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [reducedMotion] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  const [finished, setFinished] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const readyRef = useRef(ready);

  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setFinished(true),
      reducedMotion ? REDUCED_INTRO_MS : INTRO_MS,
    );
    return () => window.clearTimeout(timer);
  }, [reducedMotion]);

  useEffect(() => {
    if (finished && ready) setLeaving(true);
  }, [finished, ready]);

  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(onDone, FADE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, onDone]);

  const skip = useCallback(() => {
    if (ready) setLeaving(true);
    else onDone();
  }, [ready, onDone]);

  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let { ctx, width, height } = sizeCanvas(canvas);
    if (!ctx) return;
    let done = reducedMotion;
    let frame = 0;

    const onResize = () => {
      ({ ctx, width, height } = sizeCanvas(canvas));
      if (ctx && (reducedMotion || done)) drawMark(ctx, width, height);
    };
    window.addEventListener('resize', onResize);

    if (reducedMotion) {
      drawMark(ctx, width, height);
    } else {
      // Signals index.html already drew before the bundle loaded.
      const boot =
        (window as { __welcomeBootSignals?: Array<[number, number]> }).__welcomeBootSignals ?? [];
      const signals: Signal[] = [];
      for (let dot = 0; dot < DOTS.length; dot++) {
        for (let i = 0; i < SIGNALS_PER_DOT; i++) {
          const seeded = boot[signals.length];
          signals.push({
            sx: seeded ? seeded[0] : Math.random(),
            sy: seeded ? seeded[1] : Math.random(),
            bend: (Math.random() - 0.5) * 0.7,
            dot,
            appear: boot[signals.length] ? 0 : Math.random() * BLINK_MS,
            start: TRAVEL_START_MS + Math.random() * 450,
            duration: 900 + Math.random() * 380,
            phase: Math.random() * 6,
          });
        }
      }
      const arrived = new Array<number>(DOTS.length).fill(0);
      const flash = new Array<number>(DOTS.length).fill(0);
      const idle: IdleSignal[] = [];
      const startedAt = performance.now();
      let lastFrame = startedAt;
      let lastSpawn = 0;

      const fadeTrails = (c: CanvasRenderingContext2D) => {
        // Fade the last frame instead of clearing it, so moving signals leave trails.
        c.globalCompositeOperation = 'destination-out';
        c.fillStyle = 'rgba(0,0,0,0.26)';
        c.fillRect(0, 0, width, height);
        c.globalCompositeOperation = 'source-over';
      };

      const drawSignal = (c: CanvasRenderingContext2D, x: number, y: number, alpha: number) => {
        c.fillStyle = `rgba(${SIGNAL_RGB},${alpha})`;
        c.beginPath();
        c.arc(x, y, 1.7, 0, Math.PI * 2);
        c.fill();
      };

      // After the intro: the mark stays white, and while Studio loads, new
      // signals stream in and flash the dot they reach. Returns false once
      // there is nothing left to animate.
      const idleFrame = (c: CanvasRenderingContext2D, now: number, elapsed: number) => {
        const dt = now - lastFrame;
        const waiting = !readyRef.current;
        if (waiting && elapsed >= INTRO_MS && now - lastSpawn >= IDLE_SPAWN_MS) {
          lastSpawn = now;
          const [sx, sy] = edgePoint();
          idle.push({
            sx,
            sy,
            bend: (Math.random() - 0.5) * 0.6,
            dot: Math.floor(Math.random() * DOTS.length),
            start: now,
          });
        }
        const { radius, targets } = markGeometry(width, height);
        fadeTrails(c);
        for (let i = idle.length - 1; i >= 0; i--) {
          const s = idle[i];
          const u = (now - s.start) / IDLE_TRAVEL_MS;
          if (u >= 1) {
            flash[s.dot] = 1;
            idle.splice(i, 1);
            continue;
          }
          const [tx, ty] = targets[s.dot];
          const [x, y] = signalPosition(s.sx * width, s.sy * height, tx, ty, s.bend, u);
          drawSignal(c, x, y, 1);
        }
        for (let dot = 0; dot < DOTS.length; dot++) {
          const f = flash[dot];
          const [x, y] = targets[dot];
          c.save();
          c.shadowColor = `rgba(${SIGNAL_RGB},${0.9 * f})`;
          c.shadowBlur = 16 * f;
          c.fillStyle = `rgb(${Math.round(MARK_RGB[0] * (1 - f))},${Math.round(MARK_RGB[1] - 5 * f)},${Math.round(MARK_RGB[2] * (1 - f))})`;
          c.beginPath();
          c.arc(x, y, radius * (1 + 0.18 * f), 0, Math.PI * 2);
          c.fill();
          c.restore();
          flash[dot] = Math.max(0, f - dt / IDLE_FLASH_MS);
        }
        return waiting || elapsed < INTRO_MS || idle.length > 0 || flash.some((f) => f > 0);
      };

      const tick = (now: number) => {
        const c = ctx;
        if (!c) return;
        const elapsed = now - startedAt;
        if (elapsed >= CANVAS_END_MS) {
          const more = idleFrame(c, now, elapsed);
          lastFrame = now;
          if (more) {
            frame = requestAnimationFrame(tick);
          } else {
            done = true;
            drawMark(c, width, height);
          }
          return;
        }
        lastFrame = now;
        const { radius, targets } = markGeometry(width, height);
        fadeTrails(c);

        arrived.fill(0);
        for (const s of signals) {
          if (elapsed < s.appear) continue;
          const sx = s.sx * width;
          const sy = s.sy * height;
          let x = sx;
          let y = sy;
          let alpha = 1;
          if (elapsed < s.start) {
            alpha = 0.3 + 0.7 * Math.abs(Math.sin(elapsed / 170 + s.phase));
          } else {
            const u = Math.min(1, (elapsed - s.start) / s.duration);
            if (u >= 1) {
              arrived[s.dot]++;
              continue;
            }
            const [tx, ty] = targets[s.dot];
            [x, y] = signalPosition(sx, sy, tx, ty, s.bend, u);
          }
          drawSignal(c, x, y, alpha);
        }

        const white = Math.min(1, Math.max(0, (elapsed - WHITEN_START_MS) / WHITEN_MS));
        for (let dot = 0; dot < DOTS.length; dot++) {
          const fill = arrived[dot] / SIGNALS_PER_DOT;
          if (fill <= 0) continue;
          const [x, y] = targets[dot];
          c.save();
          c.shadowColor = `rgba(${SIGNAL_RGB},${0.9 * (1 - white)})`;
          c.shadowBlur = 16 * (1 - white);
          c.fillStyle = `rgb(${Math.round(MARK_RGB[0] * white)},${Math.round(245 + 5 * white)},${Math.round(MARK_RGB[2] * white)})`;
          c.beginPath();
          c.arc(x, y, radius * (0.35 + 0.65 * fill), 0, Math.PI * 2);
          c.fill();
          c.restore();
        }
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [reducedMotion]);

  return (
    <div
      className={`welcome-intro${leaving ? ' welcome-intro--leaving' : ''}${reducedMotion ? ' welcome-intro--still' : ''}`}
      onClick={skip}
      role="presentation"
    >
      <canvas ref={canvasRef} className="welcome-intro__canvas" aria-hidden="true" />
      <div
        className="welcome-intro__lockup"
        role="img"
        aria-label={`RapidSOS ${t('common.studio')}`}
      >
        <span className="welcome-intro__word welcome-intro__word--brand">RapidSOS</span>
        <span className="welcome-intro__word welcome-intro__word--product">
          {t('common.studio')}
        </span>
      </div>
      <button
        type="button"
        className="welcome-intro__skip"
        onClick={(event) => {
          event.stopPropagation();
          skip();
        }}
      >
        {t('tutorial.skip')}
      </button>
    </div>
  );
}
